import { ReactNode } from "react";

export type Props = {
  /** Whether to block access to children */
  shouldBlock: boolean;
  /** Whether data is still loading */
  isLoading: boolean;
  /** The children to render when not blocked */
  children: ReactNode;
  /** Render function for loading state */
  renderLoading?: () => ReactNode;
  /** Render function for blocked state (e.g., redirect or message) */
  renderBlocked?: () => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base gate component that conditionally renders children based on onboarding state.
 *
 * This is an unstyled component that handles the logic of showing loading,
 * blocked, or children states. Use render props to customize each state.
 *
 * @example
 * // In themes/pollinator/ui/OnboardingGate.tsx
 * import { OnboardingGateBase, type Props as OnboardingGateBaseProps } from "@core/ui";
 * import { useOnboardingGate } from "@core/features/onboarding";
 * import { Navigate } from "react-router-dom";
 * import { Loader } from "../Loader";
 *
 * export function OnboardingGate({ programId, user, children }) {
 *   const { shouldBlock, redirectPath, isLoading } = useOnboardingGate({
 *     programId,
 *     user,
 *     isEnabled: true,
 *   });
 *
 *   return (
 *     <OnboardingGateBase
 *       shouldBlock={shouldBlock}
 *       isLoading={isLoading}
 *       renderLoading={() => <Loader isFullPage />}
 *       renderBlocked={() =>
 *         redirectPath ? <Navigate to={redirectPath} replace /> : null
 *       }
 *     >
 *       {children}
 *     </OnboardingGateBase>
 *   );
 * }
 */
export function OnboardingGateBase({
  shouldBlock,
  isLoading,
  children,
  renderLoading,
  renderBlocked,
  "data-testid": testId = "onboarding-gate",
}: Props) {
  // Loading state
  if (isLoading) {
    return (
      <div data-testid={`${testId}-loading`}>
        {renderLoading ? renderLoading() : <span>Loading...</span>}
      </div>
    );
  }

  // Blocked state
  if (shouldBlock) {
    return (
      <div data-testid={`${testId}-blocked`}>
        {renderBlocked ? renderBlocked() : null}
      </div>
    );
  }

  // Render children when not blocked
  return <div data-testid={testId}>{children}</div>;
}

OnboardingGateBase.displayName = "OnboardingGateBase";

